import bbox from '@turf/bbox';
import { feature, featureCollection, Geometry, Polygon } from '@turf/helpers';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { bboxPolygon, circle, difference } from '@turf/turf';
import { Feature } from 'geojson';
import { useEffect, useMemo } from 'react';
import { MapRef } from 'react-map-gl';

import { isNullish } from '@/utils/booleans';

type UseBoundingBoxOptions = {
  geometries: Geometry[];
  padding?: Partial<{
    top: number;
    bottom: number;
    left: number;
    right: number;
  }>;
  mapRef?: MapRef | null;
};

export const useBoundingBox = ({ geometries, padding, mapRef }: UseBoundingBoxOptions) => {
  const bounds = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const bounds = calculateBounds(geometries);
    if (areBoundsValid(bounds)) return bounds;

    const completeWorldMap: BBox2d = [-170, -60, 170, 60];
    return completeWorldMap;
  }, [geometries]);

  useEffect(() => {
    mapRef?.fitBounds?.(bounds, {
      padding: { top: 0, bottom: 0, left: 0, right: 0, ...padding },
      maxZoom: 16,
    });
  }, [bounds, padding, mapRef]);

  return { bounds };
};

export const calculateBounds = (geometries: Geometry[]) => {
  const features = geometries.map((g) => feature(g));

  return bbox({
    type: 'FeatureCollection',
    features,
  }) as BBox2d;
};

const areBoundsValid = ([minX, minY, maxX, maxY]: BBox2d) => {
  let areValidBounds = true;

  const isValidX = (x: number) => x <= 180 && x >= -180;
  const isValidY = (y: number) => y <= 90 && y >= -90;

  if (!isValidX(minX)) areValidBounds = false;
  if (!isValidY(minY)) areValidBounds = false;
  if (!isValidX(maxX)) areValidBounds = false;
  if (!isValidY(maxY)) areValidBounds = false;

  return areValidBounds;
};

export const getRadiusCircleMask = ({ bounds, radius }: { bounds: BBox2d; radius: number | null }) => {
  if (isNullish(radius)) {
    return {
      circleGeoJSON: null,
      mask: null,
    };
  }

  const [minX, minY, maxX, maxY] = bounds;

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const center = [centerX, centerY];

  const circleGeoJSON = circle(center, radius, {
    steps: 64,
    units: 'kilometers',
  });

  const world = bboxPolygon([-180, -90, 180, 90]);
  return {
    circleGeoJSON,
    mask: featureCollection([difference(world, circleGeoJSON) as Feature<Polygon>]),
  };
};
